import { useEffect } from 'react';

interface SeoHeadProps {
  title: string;
  description: string;
  keywords: string;
  canonicalUrl: string;
  ogTitle?: string;
  ogDescription?: string;
  ogType?: string;
  schemaType: 'Organization' | 'WebSite' | 'WebPage' | 'Article' | 'FAQPage' | 'BreadcrumbList' | 'home';
  articleData?: {
    headline: string;
    datePublished: string;
    dateModified: string;
    author: string;
    imageUrl: string;
    category: string;
  };
  breadcrumbData?: Array<{ name: string; url: string }>;
}

export default function SeoHead({
  title,
  description,
  keywords,
  canonicalUrl,
  ogTitle,
  ogDescription,
  ogType = 'website',
  schemaType,
  articleData,
  breadcrumbData,
}: SeoHeadProps) {
  useEffect(() => {
    document.title = title;

    const setMeta = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('name', name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    const setProperty = (property: string, content: string) => {
      let el = document.querySelector(`meta[property="${property}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('property', property);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('description', description);
    setMeta('keywords', keywords);

    const linkEl = document.querySelector('link[rel="canonical"]');
    if (linkEl) linkEl.setAttribute('href', canonicalUrl);

    setProperty('og:title', ogTitle || title);
    setProperty('og:description', ogDescription || description);
    setProperty('og:type', ogType);
    setProperty('og:url', canonicalUrl);

    const existingScript = document.getElementById('json-ld');
    if (existingScript) existingScript.remove();

    const script = document.createElement('script');
    script.id = 'json-ld';
    script.type = 'application/ld+json';

    let schema: Record<string, unknown>;

    switch (schemaType) {
      case 'home': {
        schema = {
          '@context': 'https://schema.org',
          '@graph': [
            {
              '@type': 'Organization',
              name: '九游娱乐',
              url: 'https://www.jiuyou.com',
              description: '九游娱乐是值得信赖的综合游戏娱乐平台，汇聚海量精品游戏、丰厚福利活动与极致娱乐体验。',
              contactPoint: {
                '@type': 'ContactPoint',
                telephone: '+86-400-888-9999',
                contactType: 'customer service',
                availableLanguage: 'Chinese',
                areaServed: 'CN',
              },
              sameAs: ['https://www.jiuyou.com'],
            },
            {
              '@type': 'WebSite',
              name: '九游娱乐',
              url: 'https://www.jiuyou.com',
              description: '官方正版游戏娱乐平台',
              potentialAction: {
                '@type': 'SearchAction',
                target: 'https://www.jiuyou.com/search?q={search_term_string}',
                'query-input': 'required name=search_term_string',
              },
            },
            {
              '@type': 'WebPage',
              name: '九游娱乐 - 首页',
              description: description,
              url: canonicalUrl,
              isPartOf: {
                '@type': 'WebSite',
                name: '九游娱乐',
                url: 'https://www.jiuyou.com',
              },
            },
          ],
        };
        break;
      }
      case 'Organization':
        schema = {
          '@context': 'https://schema.org',
          '@type': 'Organization',
          name: '九游娱乐',
          url: canonicalUrl,
          description: description,
          contactPoint: {
            '@type': 'ContactPoint',
            telephone: '+86-400-888-9999',
            contactType: 'customer service',
            availableLanguage: 'Chinese',
          },
        };
        break;
      case 'Article':
        schema = {
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: articleData?.headline || title,
          description: description,
          datePublished: articleData?.datePublished || '',
          dateModified: articleData?.dateModified || articleData?.datePublished || '',
          author: {
            '@type': 'Person',
            name: articleData?.author || '九游小编',
          },
          image: articleData?.imageUrl || '',
          articleSection: articleData?.category || '',
          publisher: {
            '@type': 'Organization',
            name: '九游娱乐',
          },
        };
        break;
      case 'BreadcrumbList':
        schema = {
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: (breadcrumbData || []).map((item, index) => ({
            '@type': 'ListItem',
            position: index + 1,
            name: item.name,
            item: item.url,
          })),
        };
        break;
      case 'FAQPage':
        schema = {
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: (breadcrumbData || []).map((item) => ({
            '@type': 'Question',
            name: item.name,
            acceptedAnswer: {
              '@type': 'Answer',
              text: item.url,
            },
          })),
        };
        break;
      default:
        schema = {
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          name: title,
          description: description,
          url: canonicalUrl,
        };
    }

    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);

    return () => {
      const s = document.getElementById('json-ld');
      if (s) s.remove();
    };
  }, [title, description, keywords, canonicalUrl, ogTitle, ogDescription, ogType, schemaType, articleData, breadcrumbData]);

  return null;
}